import { useParams } from "react-router";
import {
  useHtzWorkOrderShowQuery,
  useHtzWorkOrderUpdateWorkOrderStateMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { WorkOrderStateDropdown } from "@/services/backend/relationships/components/work-order-state-dropdown";
import { SignatureButton } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/signature-button";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/work-order-pdf";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { WorkOrderProviders } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-providers";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/htz/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { Separator } from "@/shared/components/ui/separator";
import { SetWorkOrderImmutableDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/set-work-order-immutable-dialog";
import { ConnectInvoiceExportDialogHtz } from "@/services/backend/connect/components/connect-invoice-export-htz";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderCompletionRoute() {
  const { workOrderId } = useParams();

  return (
    <WorkOrderProviders>
      <WorkOrderCompletionPage workOrderId={workOrderId!} />
    </WorkOrderProviders>
  );
}

function WorkOrderCompletionPage({ workOrderId }: { workOrderId: string }) {
  const {
    data: workOrder,
    isLoading,
    error,
  } = useHtzWorkOrderShowQuery({
    id: workOrderId,
  });

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Executed,
    FulfillmentState.PartiallyExecuted,
    FulfillmentState.Completed,
    FulfillmentState.Cancelled,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="completion">
      {t("Nachbereitung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error ? <RTKQueryErrorAlert error={error} /> : null}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && <Page workOrder={workOrder} />}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  // A written down work order does not move anymore, an executed one is
  // cancelled or completed, and a completed or cancelled one can only be
  // planned again.
  const stateTransitions = isImmutable(workOrder)
    ? []
    : [FulfillmentState.Completed, FulfillmentState.Cancelled].includes(
          workOrder.workOrderState,
        )
      ? [FulfillmentState.Planning]
      : [FulfillmentState.Cancelled, FulfillmentState.Completed];

  const links = [
    {
      to: "data",
      label: t("Arbeitsschein"),
      fieldName: [
        "referenceNumber",
        "executingBranchId",
        "beginDate",
        "endDate",
        "teamLeader",
        "internalComment",
        "address",
        "contact",
        "representative",
      ],
    },
    {
      to: "positions",
      label: t("Leistungen"),
      fieldName: ["positions"],
    },
    {
      to: "laboratory",
      label: t("Labor"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={workOrder} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <DocumentPageActions>
          {!isImmutable(workOrder) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <SignatureButton workOrderId={workOrder.id} />
              <Separator orientation="vertical" className="min-h-8" />
              <SetWorkOrderImmutableDialog workOrder={workOrder} />
              <WorkOrderPdf workOrder={workOrder} />
              <ConnectInvoiceExportDialogHtz workOrder={workOrder} />
              <Separator orientation="vertical" className="min-h-8" />
            </>
          )}
          {isImmutable(workOrder) && <WorkOrderPdf workOrder={workOrder} />}
          <WorkOrderStateDropdown
            workOrder={workOrder}
            mutation={useHtzWorkOrderUpdateWorkOrderStateMutation}
            transitions={stateTransitions}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
